import React, { FC, useRef } from 'react'
import { Property } from 'csstype'
import styled, { css } from 'styled-components'
import { Field as FinalField, UseFieldConfig } from 'react-final-form'
import { Flex } from '../box'
import { Button, ButtonProps } from '../button'
import { VerticalDivider } from '../divider'
import { Card } from '../card'
import { ThemeColors } from '../theme/colors'
import { useFocus } from './hooks'
import { Field, Props as FieldProps } from './Field'

export type TextFieldProps = Omit<FieldProps, 'onChange'> & {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  textOverflow?: Property.TextOverflow
}

const InputStyled = styled.input<{
  fontSize?: number
  placeholderColor?: ThemeColors
  textOverflow?: Property.TextOverflow
}>`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: none;
  padding: 0;
  font-weight: inherit;
  color: ${props => props.theme.colors['neutral-dark-100']};
  font-size: ${props => props.theme.fontSizes[props.fontSize ?? 14]};
  font-family: ${props => props.theme.fonts['primary']};
  ${({ textOverflow }) =>
    textOverflow &&
    css`
      text-overflow: ${textOverflow};
    `}
  ${({ theme }) =>
    css`
      ::placeholder {
        color: ${theme.colors['neutral-dark-24']};
        font-size: ${props => props.theme.fontSizes[14]};
      }
    `}
`

export const TextField: FC<TextFieldProps> = React.forwardRef(
  (
    {
      focused: focus,
      onFocus,
      onBlur,
      value,
      defaultValue,
      onChange,
      placeholder,
      id,
      name,
      'aria-label': ariaLabel,
      'data-test-id': dataTestId,
      onWhite = true,
      label,
      fontWeight,
      fontSize,
      h,
      autoFocus,
      textOverflow,
      ...p
    }: TextFieldProps,
    ref
  ) => {
    const [focused, handleFocus, handleBlur] = useFocus(focus, onFocus, onBlur)
    return (
      <Field
        {...p}
        fontWeight={fontWeight}
        id={id}
        onWhite={onWhite}
        h={h || (label ? 11 : 8)}
        label={label}
        focused={focused}
        value={value}
        placeholder={placeholder}
      >
        <InputStyled
          {...p}
          h={h}
          label={label}
          data-test-id={dataTestId}
          aria-label={ariaLabel}
          ref={ref as any}
          value={value}
          defaultValue={defaultValue}
          id={id}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          fontWeight={fontWeight}
          fontSize={fontSize}
          textOverflow={textOverflow}
        />
      </Field>
    )
  }
)

TextField.displayName = 'TextField'

type FinalTextFieldProps = Omit<TextFieldProps, 'name'> & { name: string } & Pick<
    UseFieldConfig<string>,
    'parse'
  >

export const FinalTextField: FC<FinalTextFieldProps> = ({
  name,
  id,
  parse,
  ...props
}: FinalTextFieldProps) => {
  return (
    <FinalField name={name} id={id} parse={parse}>
      {({ input, meta }) => (
        <TextField {...props} {...input} id={id} error={meta.touched && meta.error} />
      )}
    </FinalField>
  )
}

type TextFieldWithFloatingButton = {
  buttonLabel: string
  buttonProps?: ButtonProps
  css?: any
  secondButton?: React.ReactNode
} & TextFieldProps

type TextFieldWithCopyProps = {
  onCopy?: () => void
  secondButton?: React.ReactNode
} & TextFieldProps

const ButtonContainer = styled(Card)`
  position: absolute;
  right: 8px;
  top: calc(50% - 20px);
  padding: 0;
  display: flex;
`

export const TextFieldWithFloatingButton = React.forwardRef<
  HTMLInputElement,
  TextFieldWithFloatingButton
>(({ secondButton: SecondButton, onClick, buttonLabel, buttonProps, ...props }, ref) => {
  return (
    <Flex position='relative' w='100%'>
      <TextField w='100%' ref={ref} {...props} />
      <ButtonContainer h={8}>
        <Button
          borderRadius='none'
          boxShadow='none'
          bg='none'
          color='teal-dark-100'
          onClick={onClick}
          {...buttonProps}
        >
          {buttonLabel}
        </Button>
        {SecondButton && (
          <>
            <VerticalDivider />
            {SecondButton}
          </>
        )}
      </ButtonContainer>
    </Flex>
  )
})

export const TextFieldWithCopy = ({
  onCopy,
  secondButton: AdditionalButton,
  ...props
}: TextFieldWithCopyProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const onCopyClick = () => {
    inputRef?.current?.select()
    inputRef?.current?.focus()
    document.execCommand('copy')
    onCopy && onCopy()
  }

  return (
    <TextFieldWithFloatingButton
      ref={inputRef}
      readOnly
      buttonLabel='Copy'
      onClick={onCopyClick}
      secondButton={AdditionalButton}
      {...props}
    />
  )
}

type TextFieldWithCopyAndPreviewProps = {
  link?: string
  value: string
} & TextFieldWithCopyProps

export const TextFieldWithCopyAndPreview = (props: TextFieldWithCopyAndPreviewProps) => {
  return (
    <TextFieldWithCopy
      {...props}
      secondButton={
        <a href={props.link || props.value || '#'} rel='noopener noreferrer' target='_blank'>
          <Button boxShadow='none' borderRadius='none' bg='none' as='div'>
            Preview
          </Button>
        </a>
      }
    />
  )
}
